import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';

interface PipedriveIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const PipedriveIntegrationSetup: React.FC<PipedriveIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [showApiToken, setShowApiToken] = useState(false);
  
  const [formData, setFormData] = useState({
    apiToken: '',
    companyDomain: '',
    webhookSecret: '',
    enableWebhooks: false
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.apiToken) {
      setError('API Token is required');
      return false;
    }
    if (!formData.companyDomain) {
      setError('Company Domain is required');
      return false;
    }
    return true;
  };

  const testConnection = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      // Mock connection test for now - replace with actual implementation later
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      
      const result = {
        success: true,
        message: 'Connection successful',
        companyInfo: {
          name: `${formData.companyDomain} Company`,
          currency: 'USD'
        }
      };
      
      setTestResult(result);
      
      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      // Create a mock integration object that matches what IntegrationDashboard expects
      const mockIntegration = {
        getConfig: () => ({
          settings: {
            customSettings: {
              companyDomain: formData.companyDomain,
              enableWebhooks: formData.enableWebhooks,
              webhookSecret: formData.webhookSecret,
              apiToken: formData.apiToken ? '***' : undefined
            }
          }
        })
      };

      onIntegrationCreated(mockIntegration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Pipedrive Connection</h3>
        <p className="text-content-secondary">
          Connect your Pipedrive account to sync persons, organizations, and deals with your email marketing platform.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Before you start</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              You'll need to get your API token from Pipedrive:
            </p>
            <ol className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-decimal list-inside">
              <li>Go to your Pipedrive account settings</li>
              <li>Navigate to <strong>Personal preferences → API</strong></li>
              <li>Copy your personal API token</li>
              <li>Note your company domain (e.g., yourcompany.pipedrive.com)</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company Domain *
          </label>
          <div className="flex">
            <input
              type="text"
              value={formData.companyDomain}
              onChange={(e) => handleInputChange('companyDomain', e.target.value)}
              placeholder="yourcompany"
              className="flex-1 px-3 py-2 border border-line-strong rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="inline-flex items-center px-3 py-2 border border-l-0 border-line-strong bg-gray-50 dark:bg-dark-700 text-content-muted text-sm rounded-r-lg">
              .pipedrive.com
            </span>
          </div>
          <p className="text-xs text-content-muted mt-1">
            Your Pipedrive company domain (without .pipedrive.com)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Token *
          </label>
          <div className="relative">
            <input
              type={showApiToken ? 'text' : 'password'}
              value={formData.apiToken}
              onChange={(e) => handleInputChange('apiToken', e.target.value)}
              placeholder="Enter your Pipedrive API token"
              className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowApiToken(!showApiToken)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
            >
              {showApiToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-content-muted mt-1">
            Found in Personal preferences → API in your Pipedrive account
          </p>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
        <h4 className="font-medium text-content mb-2">What will be synced?</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Persons</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Contact information</li>
              <li>Email addresses</li>
              <li>Phone numbers</li>
              <li>Custom fields</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Organizations</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Company details</li>
              <li>Contact information</li>
              <li>Custom fields</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Activities</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Email engagement</li>
              <li>Campaign interactions</li>
              <li>Activity timeline</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Test Connection
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Connection Successful!</h3>
        <p className="text-content-secondary">
          Configure additional settings for your Pipedrive integration.
        </p>
      </div>

      {testResult && testResult.success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <p className="text-green-800 dark:text-green-300 font-medium">Connected to Pipedrive</p>
          </div>
          {testResult.companyInfo && (
            <div className="text-sm text-green-700 dark:text-green-300">
              <p><strong>Company:</strong> {testResult.companyInfo.name || 'Pipedrive Account'}</p>
              {testResult.companyInfo.currency && (
                <p><strong>Currency:</strong> {testResult.companyInfo.currency}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 border border-line rounded-lg">
          <div>
            <h4 className="font-medium text-content">Enable Webhooks</h4>
            <p className="text-sm text-content-secondary">
              Get real-time updates when persons, organizations, or deals change
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableWebhooks}
              onChange={(e) => handleInputChange('enableWebhooks', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-dark-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after: after:border-line-strong after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {formData.enableWebhooks && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Webhook Secret
            </label>
            <input
              type="text"
              value={formData.webhookSecret}
              onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
              placeholder="Enter a secure webhook secret (min 16 characters)"
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-content-muted mt-1">
              Used to verify webhook authenticity. Generate a random string of at least 16 characters.
            </p>
          </div>
        )}
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 dark:text-yellow-300">Integration Features</h4>
            <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1">
              This integration will sync:
            </p>
            <ul className="text-yellow-800 dark:text-yellow-300 text-sm mt-2 list-disc list-inside">
              <li>Persons and Organizations bidirectionally</li>
              <li>Email engagement activities to Pipedrive</li>
              <li>Deal stage updates and notes</li>
              <li>Custom field mappings</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Back
        </button>
        <button
          onClick={createIntegration}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Integration
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-content">Pipedrive Integration Setup</h2>
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              1
            </span>
            <span className="w-8 h-px bg-gray-300"></span>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              2
            </span>
          </div>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};